import { div, button } from '../utils/dom';
import type { Level } from '../types';


interface LevelSwitcherCallbacks {
  onLevelChange: (levelId: string) => void;
}

const CLASSNAME_LEVEL_SWITCHER = 'mapplic-level-switcher';
const CLASSNAME_LEVEL_SWITCHER_BTN = 'mapplic-level-switcher__btn';

/**
 * LevelSwitcher - Barrierefreier Etagenwechsler
 *
 * Zwei Buttons für EG/UG mit aria-pressed Toggle-Pattern.
 * Enthält eine Live Region für Screenreader-Ankündigungen.
 */
export class LevelSwitcher {
  public readonly rootElem: HTMLElement;
  private buttons: Map<string, HTMLButtonElement> = new Map();
  private statusElem!: HTMLElement;
  private levels: Level[];
  private currentLevelId: string;
  private onLevelChange?: (levelId: string) => void;

  constructor(
    levels: Level[],
    initialLevelId: string,
    onLevelChange?: (levelId: string) => void
  ) {
    this.levels = levels;
    this.currentLevelId = initialLevelId;
    this.onLevelChange = onLevelChange;

    this.rootElem = this.createDOM();
    this.bindEvents();
  }

  private createDOM(): HTMLElement {
    const wrapper = div({
      className: CLASSNAME_LEVEL_SWITCHER
    });

    // Live Region (visuell versteckt, für Screenreader)
    this.statusElem = div({
      className: 'mapplic-sr-only',
      attributes: {
        'aria-live': 'polite',
        'aria-atomic': 'true'
      }
    });
    wrapper.appendChild(this.statusElem);

    // Buttons für jede Etage erstellen
    this.levels.forEach(level => {
      const isActive = level.id === this.currentLevelId;

      const btn = button({
        className: CLASSNAME_LEVEL_SWITCHER_BTN,
        text: level.title,
        attributes: {
          type: 'button',
          'aria-pressed': isActive ? 'true' : 'false',
          'data-level': level.id
        }
      });

      this.buttons.set(level.id, btn);
      wrapper.appendChild(btn);
    });

    return wrapper;
  }

  private bindEvents(): void {
    this.buttons.forEach((btn, levelId) => {
      btn.addEventListener('click', () => this.handleClick(levelId));
    });
  }

  /**
   * Wird bei User-Klick auf Button aufgerufen.
   * Aktualisiert UI und ruft Callback auf.
   */
  private handleClick(levelId: string): void {
    if (levelId === this.currentLevelId) return;

    this.updateUI(levelId);

    this.onLevelChange?.(levelId);
  }

  /**
   * Wird vom Parent aufgerufen bei programmatischem Level-Wechsel
   * (z.B. wenn eine Location auf einem anderen Level geklickt wird).
   * Aktualisiert nur die UI, KEIN Callback.
   */
  public setLevel(levelId: string): void {
    if (levelId === this.currentLevelId) return;
    this.updateUI(levelId);
  }

  /**
   * Aktualisiert Button-States und Live Region
   */
  private updateUI(levelId: string): void {
    const level = this.levels.find(l => l.id === levelId);
    if (!level) return;

    this.currentLevelId = levelId;

    // Buttons aktualisieren
    this.buttons.forEach((btn, id) => {
      btn.setAttribute('aria-pressed', id === levelId ? 'true' : 'false');
    });

    // Live Region ankündigen
    this.statusElem.textContent = `${level.title} wird angezeigt`;
  }
}
